import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { PiClipboardTextBold, PiExamBold, PiFolderOpenBold, PiLinkBold } from "react-icons/pi";
import client from "../../api/client";
import { submissionViewerPath } from "../../utils/submissionFile";

const GRADE_COLOR = {
  A: "bg-emerald-50 text-emerald-700 border border-emerald-100",
  B: "bg-blue-50 text-blue-700 border border-blue-100",
  C: "bg-amber-50 text-amber-700 border border-amber-100",
  D: "bg-orange-50 text-orange-700 border border-orange-100",
  F: "bg-rose-50 text-rose-700 border border-rose-100",
};

function GradeBadge({ grade }) {
  if (!grade) {
    return (
      <span className="px-2.5 py-1 rounded-md text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-100">
        Pending Review
      </span>
    );
  }
  return (
    <span className={`px-2.5 py-1 rounded-md text-xs font-semibold ${GRADE_COLOR[grade] || "bg-gray-50 text-gray-700"}`}>
      Grade: {grade}
    </span>
  );
}

function EmptyState({ icon, title, subtitle }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 py-16 text-center shadow-sm">
      <p className="text-4xl mb-3">{icon}</p>
      <h3 className="font-semibold text-gray-700 text-base">{title}</h3>
      <p className="text-sm text-gray-400 mt-1">{subtitle}</p>
    </div>
  );
}

function AssignmentCard({ item }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6 flex flex-col md:flex-row md:items-center justify-between gap-4 shadow-sm">
      <div className="flex-1 space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="bg-gray-100 text-gray-600 text-xs font-semibold px-2.5 py-1 rounded-md">
            {item.class_topic || "Class"}
          </span>
          <GradeBadge grade={item.grade} />
        </div>
        <h3 className="text-base font-bold text-gray-800">{item.exercise_title || "Assignment"}</h3>
        {!item.file_link && !item.file_download_url && item.answer_text && (
          <p className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3 whitespace-pre-wrap">{item.answer_text}</p>
        )}
        {item.instructor_feedback && (
          <p className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3">{item.instructor_feedback}</p>
        )}
        <div className="flex items-center gap-3 text-xs text-gray-400 flex-wrap">
          {item.class_date && <span>Class: {new Date(item.class_date).toLocaleDateString()}</span>}
          <span>Submitted: {new Date(item.submitted_at).toLocaleDateString()}</span>
        </div>
      </div>
      {(item.file_download_url || item.file_link) && (
        <a
          href={item.file_download_url ? submissionViewerPath(item) : item.file_link}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 text-xs font-semibold text-[#294a93] bg-[#294a93]/10 hover:bg-[#294a93]/20 rounded-xl px-4 py-2.5 transition-colors flex-shrink-0"
        >
          <PiLinkBold size={16} />
          View Submission
        </a>
      )}
    </div>
  );
}

function AssessmentCard({ item }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6 flex flex-col md:flex-row md:items-center justify-between gap-4 shadow-sm">
      <div className="flex-1 space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className={`px-2.5 py-1 rounded-md text-xs font-semibold ${
              item.passed
                ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
                : "bg-rose-50 text-rose-700 border border-rose-100"
            }`}
          >
            {item.passed ? "Passed" : "Not Passed"}
          </span>
          <span className="text-xs font-semibold text-gray-500">Score: {item.score}%</span>
        </div>
        <h3 className="text-base font-bold text-gray-800">{item.assessment_title || "Assessment"}</h3>
        <p className="text-xs text-gray-400">
          Attempted: {item.submitted_at ? new Date(item.submitted_at).toLocaleString() : "—"}
        </p>
      </div>
    </div>
  );
}

function ProjectCard({ item }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6 flex flex-col md:flex-row md:items-center justify-between gap-4 shadow-sm">
      <div className="flex-1 space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="bg-gray-100 text-gray-600 text-xs font-semibold px-2.5 py-1 rounded-md">
            {item.project_name || "Project"}
          </span>
          <GradeBadge grade={item.grade} />
        </div>
        <h3 className="text-base font-bold text-gray-800">{item.title}</h3>
        {item.instructor_feedback && (
          <p className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3">{item.instructor_feedback}</p>
        )}
        <p className="text-xs text-gray-400">Submitted: {new Date(item.submitted_at).toLocaleDateString()}</p>
      </div>
      {(item.file_download_url || item.file_link) && (
        <a
          href={item.file_download_url ? submissionViewerPath(item) : item.file_link}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 text-xs font-semibold text-[#294a93] bg-[#294a93]/10 hover:bg-[#294a93]/20 rounded-xl px-4 py-2.5 transition-colors flex-shrink-0"
        >
          <PiLinkBold size={16} />
          View Submission
        </a>
      )}
    </div>
  );
}

export default function GradesPage() {
  const [data, setData] = useState({ assignments: [], assessments: [], projects: [] });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("assignment");

  useEffect(() => {
    const token = localStorage.getItem("nd-rest-tkn");
    if (token) client.defaults.headers.Authorization = `Bearer ${token}`;
    client.get("/me/grades")
      .then((res) => setData(res.data))
      .catch(() => toast.error("Failed to load your grades."))
      .finally(() => setLoading(false));
  }, []);

  const tabs = [
    { key: "assignment", label: "Assignment", icon: PiClipboardTextBold, count: data.assignments.length },
    { key: "assessment",  label: "Assessment",  icon: PiExamBold,          count: data.assessments.length },
    { key: "project",     label: "Projects",     icon: PiFolderOpenBold,    count: data.projects.length },
  ];

  return (
    <div className="py-8 px-4 md:px-8 max-w-5xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Grades</h1>
        <p className="text-sm text-gray-500 mt-1">
          Your assignment, assessment, and project results in one place.
        </p>
      </div>

      <div className="flex border-b border-gray-200 mb-6">
        {tabs.map(({ key, label, icon: Icon, count }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-all flex items-center gap-2 ${
              tab === key
                ? "border-[#294a93] text-[#294a93]"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            <Icon size={16} />
            {label}
            <span className="ml-1 bg-gray-100 text-gray-600 text-xs font-semibold px-2 py-0.5 rounded-full">
              {count}
            </span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-2xl border border-gray-100 p-6 animate-pulse flex justify-between">
              <div className="space-y-2 w-1/3">
                <div className="h-4 bg-gray-200 rounded w-3/4" />
                <div className="h-3 bg-gray-100 rounded w-1/2" />
              </div>
              <div className="h-10 bg-gray-100 rounded w-24" />
            </div>
          ))}
        </div>
      ) : tab === "assignment" ? (
        data.assignments.length === 0 ? (
          <EmptyState icon="📋" title="No assignments yet" subtitle="Assignments you submit will show up here once graded." />
        ) : (
          <div className="grid gap-4">{data.assignments.map((a) => <AssignmentCard key={a.id} item={a} />)}</div>
        )
      ) : tab === "assessment" ? (
        data.assessments.length === 0 ? (
          <EmptyState icon="📝" title="No assessments attempted yet" subtitle="Your assessment scores will show up here after you attempt one." />
        ) : (
          <div className="grid gap-4">{data.assessments.map((a) => <AssessmentCard key={a.uuid} item={a} />)}</div>
        )
      ) : (
        data.projects.length === 0 ? (
          <EmptyState icon="📁" title="No projects submitted yet" subtitle="Projects you submit and their feedback will show up here." />
        ) : (
          <div className="grid gap-4">{data.projects.map((p) => <ProjectCard key={p.id} item={p} />)}</div>
        )
      )}
    </div>
  );
}
